import { gql } from "@outschool/ui-apollo";

const ClassDetailsQuery_Meeting = gql`
  fragment ClassDetailsQuery_Meeting on Meeting {
    uid
    start_time
    end_time
    deleted_at
    created_at
  }
`;

export const ClassDetailsQuery_Section = gql`
  fragment ClassDetailsQuery_Section on Section {
    uid
    activity_uid
    allowsLateEnrollment
    canceled_at
    created_at
    currentUserCanManage
    currentUserIsOwner
    deleted_at
    details {
      autoScheduledDraft
      onlineClassroom {
        type
        url
      }
    }
    duration_minutes
    end_time
    filledSpaceCount
    isBuyable
    isInProgress
    isPublished
    leader {
      uid
      name
    }
    meetings(startAfter: $startAfter) {
      ...ClassDetailsQuery_Meeting
    }
    meetingTimes {
      dayOfWeek
      hour
      minute
    }
    nextOngoingMeeting {
      ...ClassDetailsQuery_Meeting
    }
    ongoing_stopped_at
    price_cents
    progress {
      currentWeek
      pastMeetingCount
      remainingMeetingCount
      totalMeetingCount
      nextMeeting {
        start_time
      }
    }
    published_at
    start_time
    size_max
    size_min
    updated_at
    usesOutschoolVideoChat
    validEnrollmentCount
  }
  ${ClassDetailsQuery_Meeting}
`;

const ClassDetailsQuery_Attachment = gql`
  fragment ClassDetailsQuery_Attachment on Attachment {
    uid
    file {
      filename
      mimetype
      size
      url
      thumbUrl
    }
    conversionStatus
    created_at
  }
`;

const ClassDetailsQuery_Leader = gql`
  fragment ClassDetailsQuery_Leader on Leader {
    uid
    approved
    averageActivityStarRating
    cityState
    created_at
    details {
      about
      countryOfResidence
      headline
      headlineTranslated
    }
    educatorTiers
    hasAvailabilitySet
    hasBackgroundCheckExpired
    is_crosslister
    leader_link
    name
    ownerOfSellerOrg {
      uid
    }
    photo
    publishedClassCount
    reviewCount
    socialOptOut
    successfulClassCount
    suspended_at
    teacher_approved_at
    teacherForSellerOrg {
      uid
      name
    }
    timeZone
    video {
      ...ClassDetailsQuery_Attachment
    }
    credentials {
      uid
      degreeType
      degreeSubject
      certificateDiscipline
      otherType
      certificateState
      name
      degreeInstitution
      degreeAdditionalInfo
    }
    leaderClassLinks {
      uid
      title
      isOrdered
      bundleElements {
        orderInBundle
        activity {
          uid
        }
      }
    }
  }
  ${ClassDetailsQuery_Attachment}
`;

const ClassDetailsQuery_SampleClubPost = gql`
  fragment ClassDetailsQuery_SampleClubPost on SampleClubPost {
    text
    attachments {
      uid
      file {
        filename
        mimetype
        size
        url
      }
    }
    video {
      uid
      file {
        filename
        mimetype
        size
        url
      }
    }
    comments {
      text
    }
  }
`;

const ClassDetailsQuery_Activity = gql`
  fragment ClassDetailsQuery_Activity on Activity {
    uid
    age_max
    age_min
    grade_level_max
    grade_level_min
    url
    path
    allows_late_enrollments_for_fixed_length_classes
    allows_recurring_payment
    allowsFutureOngoingEnrollments
    has_building_content
    approvalStatus {
      action
      message_to_teacher
    }
    attributes {
      key
      value
    }
    archived_at
    assessment
    autoSchedulingDaysNoticeMin
    autoSchedulingDaysNoticeMax
    averageStarRating
    canEnroll
    classFormat
    class_experience
    clubGuidelines {
      text
    }
    clubSection {
      uid
      validEnrollmentCount
      isBuyable
    }
    completedEnrollmentCount
    details {
      asyncTime
      photo
    }
    disclaimer
    duration_minutes
    duration_weeks
    english_proficiency_level
    esaStatus(esaState: $esaState)
    userApprovedFundingPrograms
    externalResources {
      uid
      name
      url
    }
    first_listed_at
    has_external_resources
    hasTeacherSchedule
    homework
    grading_policy
    is_ongoing_weekly
    is_online
    isAutoSchedulingEnabled
    allowWaitlist
    isClub
    isFlexSchedule
    isOneTime
    isPublished
    isRestricted
    isSelfPaced
    isSoldOut
    languageOfInstruction
    learning_goals
    learningGoals
    leader {
      ...ClassDetailsQuery_Leader
    }
    materials
    meetingCount
    parental_guidance
    prerequisites
    price_cents
    priceCredits
    perWeeklyCourseCredits
    published_at
    refundPolicy {
      name
      description
      type
    }
    relatedCategories {
      uid
      slug
      basePath
      nameTitleCased
    }
    requested_listing_at
    reviewCount
    sampleClubPosts {
      ...ClassDetailsQuery_SampleClubPost
    }
    savedClassLists {
      uid
      lastAddedToAt
    }
    size_max
    size_min
    slug_id
    sources
    subject
    suggestedWeeklyMeetings
    summary
    summaryTranslated
    summaryForLearners
    syllabus {
      uid
      published_at
      lessons {
        uid
        title
        description
        week_number
        order_within_week
        lessonNumber
        videoDurationSeconds
        assignmentsCount
        unit {
          uid
          title
          unitNumber
        }
      }
    }
    title
    titleTranslated
    upcomingAvailableSectionCount
    upcomingSectionCount
    activeSections
    upsell_activity_uid
    user_uid
    video {
      ...ClassDetailsQuery_Attachment
    }
    proficiency_level {
      level
      text
    }
    wasPublishedInThePast
    weekly_meetings
    welcomeAttachmentCount
    why_me
    isIncludedWithMembership
  }
  ${ClassDetailsQuery_Attachment}
  ${ClassDetailsQuery_Leader}
  ${ClassDetailsQuery_SampleClubPost}
`;

export const ClassDetailsQueryNoSections = gql`
  query ClassDetailsNoSections($uid: ID, $slugId: String, $esaState: String) {
    activity(uid: $uid, slugId: $slugId) {
      ...ClassDetailsQuery_Activity
    }
  }
  ${ClassDetailsQuery_Activity}
`;
